import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CreateLeagueForm from '@/pages/ligas/components/CreateLeagueForm';
import BankrollCharts from '@/pages/ligas/components/BankrollCharts';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────
interface League {
  id: string;
  name: string;
  sport: string;
  type: string;
  bankroll: number;
  max_members: number;
  start_date: string;
  end_date: string;
  description: string;
  access_code: string | null;
  status: string;
  total_predictions: number;
  leader_roi: number;
  tags: string[];
  created_at: string;
  admin_id: string | null;
  admin?: { username: string; avatar_url: string } | null;
  member_count?: number;
}

interface LeagueMemberRank {
  user_id: string;
  current_bankroll: number;
  total_bets: number;
  won_bets: number;
  joined_at: string;
  profiles?: { username: string; avatar_url: string; hide_stats_leagues?: boolean } | null;
}

// ──────────────────────────────────────────────
// Join modal
// ──────────────────────────────────────────────
function JoinModal({
  league,
  onClose,
  onJoined,
}: {
  league: League;
  onClose: () => void;
  onJoined: () => void;
}) {
  const { isRegistered, supabaseUser, openLoginModal } = useAuth();
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleJoin = async () => {
    if (!isRegistered || !supabaseUser) {
      onClose();
      openLoginModal();
      return;
    }

    if (league.type === 'Privada') {
      if (!joinCode.trim() || joinCode.trim().toUpperCase() !== (league.access_code ?? '').toUpperCase()) {
        setError('Código incorrecto. Pide el código al administrador de la liga.');
        return;
      }
    }

    setLoading(true);
    setError('');

    const { error: insertError } = await supabase.from('league_members').insert({
      league_id: league.id,
      user_id: supabaseUser.id,
      current_bankroll: league.bankroll,
    });

    setLoading(false);

    if (insertError) {
      if (insertError.code === '23505') {
        setError('Ya eres miembro de esta liga.');
      } else {
        setError('No se pudo unir. Inténtalo de nuevo.');
      }
      return;
    }

    setSuccess(true);
    setTimeout(() => {
      onJoined();
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 w-full max-w-md">
        {success ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#00e87a]/15 mx-auto mb-3">
              <i className="ri-checkbox-circle-line text-[#00e87a] text-3xl"></i>
            </div>
            <p className="text-white font-bold text-lg">¡Te has unido!</p>
            <p className="text-[#a8b2c1] text-sm mt-1">{league.name}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold">Unirse: {league.name}</h3>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-xl text-[#ef4444] text-sm">
                {error}
              </div>
            )}

            {league.type === 'Privada' ? (
              <div className="space-y-4">
                <p className="text-[#a8b2c1] text-sm">Esta liga es privada. Necesitas el código de acceso.</p>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setError(''); }}
                  placeholder="Código de invitación..."
                  maxLength={8}
                  className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-4 py-3 placeholder-[#6b7280] focus:outline-none focus:border-[#ffd700]/40 tracking-widest font-bold"
                />
                <button
                  onClick={handleJoin}
                  disabled={loading}
                  className="w-full py-3 bg-[#ffd700] text-black font-bold rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {loading ? 'Verificando...' : 'Acceder con Código'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-[#a8b2c1] text-sm">
                  Te unirás con un bankroll de{' '}
                  <strong className="text-white">
                    {league.bankroll.toLocaleString('es-ES')} unidades virtuales
                  </strong>
                  . ¿Confirmas?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm rounded-xl cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleJoin}
                    disabled={loading}
                    className="flex-1 py-3 bg-[#ffd700] text-black font-bold text-sm rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {loading ? 'Uniéndose...' : 'Confirmar'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────
export default function LigasPage() {
  const { supabaseUser, isPremium, plan, isRegistered, openPaywallModal, openLoginModal: openLogin } = useAuth();
  const [activeTab, setActiveTab] = useState<'explorar' | 'ranking' | 'crear'>('explorar');
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [myLeagueRanking, setMyLeagueRanking] = useState<LeagueMemberRank[]>([]);
  const [myLeagues, setMyLeagues] = useState<string[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [selectedRankingLeague, setSelectedRankingLeague] = useState<string | null>(null);

  // Check if free user has reached league limit
  const isFreeLeaguesMaxed = !isPremium && myLeagues.length >= 3;

  const fetchLeagues = useCallback(async () => {
    setLoading(true);

    // Auto-expire leagues past their end_date
    const today = new Date().toISOString().split('T')[0];
    await supabase
      .from('leagues')
      .update({ status: 'finished' })
      .eq('status', 'active')
      .lt('end_date', today);

    const { data, error } = await supabase
      .from('leagues')
      .select(`
        *,
        admin:profiles!leagues_admin_id_fkey(username, avatar_url),
        league_members(count)
      `)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (!error && data) {
      const mapped = data.map((l: Record<string, unknown>) => {
        const memberArr = l.league_members as { count: number }[] | undefined;
        return {
          ...l,
          member_count: memberArr?.[0]?.count ?? 0,
        } as League;
      });
      setLeagues(mapped);
    }
    setLoading(false);
  }, []);

  const fetchMyLeagues = useCallback(async () => {
    if (!supabaseUser) return;
    const { data } = await supabase
      .from('league_members')
      .select('league_id')
      .eq('user_id', supabaseUser.id);
    if (data) setMyLeagues(data.map((r) => r.league_id as string));
  }, [supabaseUser]);

  const fetchMyRanking = useCallback(async () => {
    if (!supabaseUser || myLeagues.length === 0) return;
    setRankingLoading(true);
    const leagueId = selectedRankingLeague ?? myLeagues[0];
    const { data } = await supabase
      .from('league_members')
      .select('user_id, current_bankroll, total_bets, won_bets, joined_at, profiles(username, avatar_url, hide_stats_leagues)')
      .eq('league_id', leagueId)
      .order('current_bankroll', { ascending: false });
    if (data) setMyLeagueRanking(data as LeagueMemberRank[]);
    setRankingLoading(false);
  }, [supabaseUser, myLeagues, selectedRankingLeague]);

  useEffect(() => {
    fetchLeagues();

    // Real-time subscription for leagues
    const leagueChannel = supabase
      .channel('leagues-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leagues' }, fetchLeagues)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'league_members' }, fetchLeagues)
      .subscribe();

    return () => { supabase.removeChannel(leagueChannel); };
  }, [fetchLeagues]);

  useEffect(() => {
    fetchMyLeagues();
  }, [fetchMyLeagues]);

  useEffect(() => {
    if (activeTab === 'ranking') fetchMyRanking();
  }, [activeTab, fetchMyRanking, selectedRankingLeague]);

  const handleLeagueCreated = () => {
    setActiveTab('explorar');
  };

  const handleJoined = () => {
    fetchLeagues();
    fetchMyLeagues();
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalMembers = leagues.reduce((acc, l) => acc + (l.member_count ?? 0), 0);

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16">
        {/* Header */}
        <div className="relative bg-[#151719] border-b border-[#2a2d3a] overflow-hidden">
          <div
            className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #ffd700 0%, transparent 50%)' }}
          ></div>
          <div className="relative max-w-[1440px] mx-auto px-6 py-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-[#ffd700] text-xs font-bold tracking-widest uppercase mb-2">
                  Gamificación · Temporada 2025/26
                </p>
                <h1 className="text-white text-4xl font-black uppercase">Ligas Competitivas</h1>
                <p className="text-[#a8b2c1] text-sm mt-2">
                  Compite con bankroll virtual · Rankings automáticos por ROI
                  {!isPremium && <span className="text-[#ffd700]"> · Plan gratuito: máx. 3 ligas</span>}
                </p>
              </div>
              {!loading && (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-[#ffd700] text-2xl font-black">{leagues.length}</p>
                    <p className="text-[#6b7280] text-xs">Ligas activas</p>
                  </div>
                  <div className="w-px h-10 bg-[#2a2d3a]"></div>
                  <div className="text-right">
                    <p className="text-white text-2xl font-black">{totalMembers}</p>
                    <p className="text-[#6b7280] text-xs">Participantes</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">
          {/* Plan info banner */}
          {isRegistered && !isPremium && (
            <div className="mb-6 bg-[#151719] border border-[#2a2d3a] rounded-xl p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { plan: 'Gratuito', color: '#a8b2c1', icon: 'ri-user-line', items: ['Crear ligas públicas y privadas', 'Unirse a hasta 3 ligas', 'Estadísticas básicas (últimos 5 partidos)', 'Rankings en tiempo real'] },
                  { plan: 'Premium · 2,50€/mes', color: '#ffd700', icon: 'ri-vip-crown-line', items: ['Ligas ilimitadas', 'Puntos fuertes/débiles de rivales', 'Stats avanzadas (xG, corners 1H, BTTS…)', 'Notificaciones de caídas de cuotas', 'Gráficas avanzadas de rendimiento'] },
                  { plan: 'Elite · 5,00€/mes', color: '#ff6b35', icon: 'ri-shield-star-line', items: ['Todo Premium +', 'Ocultar tus stats a participantes', 'Exportar datos a Excel', 'Insignia exclusiva Elite'] },
                ].map((tier) => (
                  <div
                    key={tier.plan}
                    className={`rounded-xl p-3 border ${tier.plan.startsWith('Gratuito') ? 'border-[#2a2d3a] bg-white/3' : 'border-dashed'}`}
                    style={!tier.plan.startsWith('Gratuito') ? { borderColor: `${tier.color}30` } : {}}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center" style={{ color: tier.color }}>
                        <i className={`${tier.icon} text-sm`}></i>
                      </div>
                      <p className="text-xs font-bold" style={{ color: tier.color }}>{tier.plan}</p>
                      {tier.plan.startsWith('Gratuito') && (
                        <span className="ml-auto px-1.5 py-0.5 rounded bg-[#00e87a]/15 text-[#00e87a] text-[10px] font-bold">Actual</span>
                      )}
                    </div>
                    <ul className="space-y-1">
                      {tier.items.map((item) => (
                        <li key={item} className="flex items-start gap-1.5 text-[#6b7280] text-xs">
                          <div className="w-3 h-3 flex items-center justify-center mt-0.5 flex-shrink-0" style={{ color: tier.color }}>
                            <i className="ri-check-line text-xs"></i>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                    {!tier.plan.startsWith('Gratuito') && (
                      <button
                        onClick={() => openPaywallModal(tier.plan.startsWith('Premium') ? 'premium' : 'elite')}
                        className="mt-3 w-full py-1.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all"
                        style={{ background: `${tier.color}15`, color: tier.color, border: `1px solid ${tier.color}30` }}
                      >
                        Activar plan
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Free user league limit warning */}
          {isRegistered && isFreeLeaguesMaxed && (
            <div className="mb-6 bg-[#ff6b35]/5 border border-[#ff6b35]/20 rounded-xl px-5 py-4 flex items-center gap-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#ff6b35]/15 flex-shrink-0">
                <i className="ri-error-warning-line text-[#ff6b35] text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-sm">Límite de ligas alcanzado</p>
                <p className="text-[#a8b2c1] text-xs mt-1">
                  Con el plan gratuito puedes unirte a un máximo de <strong className="text-white">3 ligas</strong> simultáneamente.
                  Actualiza a Premium para ligas ilimitadas.
                </p>
              </div>
              <button
                onClick={() => openPaywallModal('premium')}
                className="px-4 py-2 bg-[#ffd700]/15 border border-[#ffd700]/30 text-[#ffd700] text-xs font-bold rounded-xl cursor-pointer hover:bg-[#ffd700]/25 transition-all whitespace-nowrap flex-shrink-0"
              >
                Ver Premium
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1 mb-8 w-fit">
            {[
              { key: 'explorar', label: 'Explorar Ligas', icon: 'ri-search-line' },
              { key: 'ranking', label: 'Mi Ranking', icon: 'ri-trophy-line' },
              { key: 'crear', label: 'Crear Liga', icon: 'ri-add-circle-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-[#ffd700]/15 text-[#ffd700]'
                    : 'text-[#6b7280] hover:text-white'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`}></i>
                </div>
                {tab.label}
                {tab.key === 'ranking' && myLeagues.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#ffd700] text-black text-xs font-black leading-none">
                    {myLeagues.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── Explorar ──────────────────────────── */}
          {activeTab === 'explorar' && (
            <>
              {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 animate-pulse">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-[#2a2d3a]"></div>
                        <div className="space-y-2 flex-1">
                          <div className="h-4 bg-[#2a2d3a] rounded w-3/4"></div>
                          <div className="h-3 bg-[#2a2d3a] rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-[#2a2d3a] rounded"></div>
                        <div className="h-3 bg-[#2a2d3a] rounded w-5/6"></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && leagues.length === 0 && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-16 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#ffd700]/10 mx-auto mb-4">
                    <i className="ri-trophy-line text-[#ffd700] text-3xl"></i>
                  </div>
                  <p className="text-white font-bold text-lg">Aún no hay ligas creadas</p>
                  <p className="text-[#6b7280] text-sm mt-2">
                    ¡Sé el primero en crear una liga y competir con otros tipsters!
                  </p>
                  <button
                    onClick={() => setActiveTab('crear')}
                    className="mt-6 px-6 py-3 bg-[#ffd700] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#f0c800] transition-all whitespace-nowrap"
                  >
                    Crear la primera liga
                  </button>
                </div>
              )}

              {!loading && leagues.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {leagues.map((league) => {
                    const memberCount = league.member_count ?? 0;
                    const adminUsername = league.admin?.username ?? 'Desconocido';
                    const adminAvatar = league.admin?.avatar_url ?? '';
                    const isMember = myLeagues.includes(league.id);
                    const isPrivate = league.type === 'Privada';

                    return (
                      <div
                        key={league.id}
                        className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6 hover:border-[#ffd700]/20 transition-all"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {adminAvatar ? (
                              <img
                                src={adminAvatar}
                                alt={adminUsername}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-[#2a2d3a] flex items-center justify-center text-white font-bold text-sm">
                                {adminUsername.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <h3 className="text-white font-bold text-base">{league.name}</h3>
                              <p className="text-[#6b7280] text-xs">Admin: @{adminUsername}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span
                              className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                                isPrivate ? 'bg-[#ff6b35]/15 text-[#ff6b35]' : 'bg-[#00e87a]/15 text-[#00e87a]'
                              }`}
                            >
                              {isPrivate ? (
                                <span className="flex items-center gap-1">
                                  <i className="ri-lock-line text-xs"></i>
                                  Privada
                                </span>
                              ) : 'Pública'}
                            </span>
                            {isMember && (
                              <span className="px-2 py-0.5 rounded-full bg-[#ffd700]/15 text-[#ffd700] text-xs font-bold">
                                Miembro
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-[#a8b2c1] text-sm leading-relaxed mb-4">
                          {league.description}
                        </p>

                        <div className="grid grid-cols-4 gap-3 mb-4">
                          {[
                            { label: 'Jugadores', value: `${memberCount}/${league.max_members}` },
                            { label: 'Bankroll', value: `${league.bankroll}u` },
                            { label: 'Deporte', value: league.sport },
                            { label: 'Líder ROI', value: league.leader_roi > 0 ? `+${league.leader_roi}%` : '—' },
                          ].map((stat) => (
                            <div key={stat.label} className="bg-[#0d0f14] rounded-lg p-2.5 text-center">
                              <p className="text-white font-bold text-sm">{stat.value}</p>
                              <p className="text-[#6b7280] text-xs">{stat.label}</p>
                            </div>
                          ))}
                        </div>

                        <div className="mb-4">
                          <div className="flex justify-between text-xs text-[#6b7280] mb-1">
                            <span>Plazas</span>
                            <span>{Math.max(0, league.max_members - memberCount)} disponibles</span>
                          </div>
                          <div className="h-1.5 bg-[#0d0f14] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#00e87a] rounded-full transition-all"
                              style={{ width: `${Math.min(100, (memberCount / league.max_members) * 100)}%` }}
                            ></div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mb-4">
                          <div className="flex flex-wrap gap-2">
                            {(league.tags ?? []).map((tag) => (
                              <span key={tag} className="px-2 py-0.5 rounded text-xs bg-[#1e2028] text-[#6b7280]">
                                #{tag}
                              </span>
                            ))}
                          </div>
                          <span className="text-[#6b7280] text-xs">
                            {formatDate(league.start_date)} → {formatDate(league.end_date)}
                          </span>
                        </div>

                        {/* Join button — now shows free league limit message instead of premium gate */}
                        {isFreeLeaguesMaxed && !isMember ? (
                          <button
                            onClick={() => openPaywallModal('premium')}
                            className="w-full py-3 text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap bg-[#ff6b35]/10 border border-[#ff6b35]/20 text-[#ff6b35] hover:bg-[#ff6b35]/20"
                          >
                            <span className="flex items-center justify-center gap-2">
                              <i className="ri-error-warning-line text-sm"></i>
                              Límite de 3 ligas (plan gratuito)
                            </span>
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              if (!isRegistered) { openLogin(); return; }
                              if (!isMember) { setSelectedLeague(league); setShowJoinModal(true); }
                            }}
                            disabled={isMember}
                            className={`w-full py-3 text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                              isMember
                                ? 'bg-[#1e2028] text-[#6b7280] cursor-not-allowed'
                                : 'bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] hover:bg-[#ffd700]/20'
                            }`}
                          >
                            {isMember ? (
                              <span className="flex items-center justify-center gap-2">
                                <i className="ri-check-line text-sm"></i>
                                Ya eres miembro
                              </span>
                            ) : isPrivate ? (
                              <span className="flex items-center justify-center gap-2">
                                <i className="ri-key-line text-sm"></i>
                                Unirse con Código
                              </span>
                            ) : (
                              'Unirse a la Liga'
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── Mi Ranking ────────────────────────── */}
          {activeTab === 'ranking' && (
            <div className="max-w-2xl">
              {!supabaseUser ? (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-12 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#ffd700]/10 mx-auto mb-3">
                    <i className="ri-lock-line text-[#ffd700] text-2xl"></i>
                  </div>
                  <p className="text-white font-bold">Inicia sesión para ver tu ranking</p>
                  <p className="text-[#6b7280] text-sm mt-1">
                    Únete a una liga y aparece en el ranking en tiempo real.
                  </p>
                </div>
              ) : myLeagues.length === 0 ? (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-12 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#ffd700]/10 mx-auto mb-3">
                    <i className="ri-trophy-line text-[#ffd700] text-2xl"></i>
                  </div>
                  <p className="text-white font-bold">Todavía no estás en ninguna liga</p>
                  <p className="text-[#6b7280] text-sm mt-1">
                    Únete a una liga pública o crea la tuya propia.
                  </p>
                  <button
                    onClick={() => setActiveTab('explorar')}
                    className="mt-4 px-5 py-2.5 bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] text-sm font-bold rounded-xl cursor-pointer hover:bg-[#ffd700]/20 transition-all whitespace-nowrap"
                  >
                    Explorar ligas
                  </button>
                </div>
              ) : (
                <>
                  {/* League selector — only members can see rankings */}
                  {myLeagues.length > 1 && (
                    <div className="mb-4 flex flex-wrap gap-2">
                      {myLeagues.map((lid) => {
                        const leagueInfo = leagues.find((l) => l.id === lid);
                        const activeId = selectedRankingLeague ?? myLeagues[0];
                        return (
                          <button
                            key={lid}
                            onClick={() => { setSelectedRankingLeague(lid); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                              activeId === lid
                                ? 'bg-[#ffd700]/15 text-[#ffd700] border border-[#ffd700]/30'
                                : 'bg-[#151719] border border-[#2a2d3a] text-[#a8b2c1] hover:text-white'
                            }`}
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-trophy-line text-xs"></i>
                            </div>
                            {leagueInfo?.name ?? `Liga ${lid.slice(0, 6)}…`}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Member-only notice */}
                  <div className="mb-4 bg-[#00e87a]/5 border border-[#00e87a]/20 rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#00e87a]/15 flex-shrink-0">
                      <i className="ri-shield-check-line text-[#00e87a] text-sm"></i>
                    </div>
                    <p className="text-[#a8b2c1] text-xs">
                      Solo los <strong className="text-white">miembros de esta liga</strong> pueden ver la clasificación.
                    </p>
                  </div>

                  <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-[#2a2d3a]">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-bold">
                            {leagues.find((l) => l.id === (selectedRankingLeague ?? myLeagues[0]))?.name ?? 'Mi liga'}
                          </h3>
                          <p className="text-[#6b7280] text-xs mt-1">Actualizado en tiempo real · Solo visible para miembros</p>
                        </div>
                        {/* Premium: rival insights */}
                        {isPremium && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] text-xs font-bold">
                            <i className="ri-eye-line text-xs"></i>
                            Stats rivales activas
                          </span>
                        )}
                      </div>
                    </div>
                    {rankingLoading ? (
                      <div className="p-8 text-center">
                        <div className="w-8 h-8 border-2 border-[#ffd700]/20 border-t-[#ffd700] rounded-full animate-spin mx-auto"></div>
                      </div>
                    ) : myLeagueRanking.length === 0 ? (
                      <div className="p-8 text-center">
                        <p className="text-[#6b7280] text-sm">No hay participantes todavía.</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[#1e2028]">
                        {myLeagueRanking.map((player, idx) => {
                          const pos = idx + 1;
                          const isMe = player.user_id === supabaseUser?.id;
                          const hasHiddenStats = !isMe && (player.profiles as { hide_stats_leagues?: boolean } | null)?.hide_stats_leagues === true;
                          const roi = ((player.current_bankroll - 1000) / 1000) * 100;
                          const hitRate = player.total_bets > 0
                            ? ((player.won_bets / player.total_bets) * 100).toFixed(1)
                            : '0.0';

                          return (
                            <div
                              key={player.user_id}
                              className={`flex items-center gap-4 px-6 py-4 transition-all ${
                                isMe ? 'bg-[#00e87a]/5 border-l-2 border-[#00e87a]' : 'hover:bg-white/[0.02]'
                              }`}
                            >
                              <div
                                className={`w-8 h-8 flex items-center justify-center rounded-full font-black text-sm flex-shrink-0 ${
                                  pos === 1
                                    ? 'bg-[#ffd700]/20 text-[#ffd700]'
                                    : pos === 2
                                    ? 'bg-white/10 text-white'
                                    : pos === 3
                                    ? 'bg-[#ff6b35]/20 text-[#ff6b35]'
                                    : 'bg-[#1e2028] text-[#6b7280]'
                                }`}
                              >
                                {pos <= 3 ? ['🥇', '🥈', '🥉'][pos - 1] : pos}
                              </div>
                              {player.profiles?.avatar_url ? (
                                <img
                                  src={player.profiles.avatar_url}
                                  alt={player.profiles?.username}
                                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-[#2a2d3a] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                  {(player.profiles?.username ?? '?').charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className={`font-semibold text-sm ${isMe ? 'text-[#00e87a]' : 'text-white'}`}>
                                    {player.profiles?.username ?? 'Usuario'}
                                    {isMe && <span className="text-xs ml-1">(Tú)</span>}
                                  </p>
                                  {/* Elite badge */}
                                  {plan === 'elite' && isMe && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-[#ff6b35]/20 text-[#ff6b35]">
                                      ELITE
                                    </span>
                                  )}
                                  {hasHiddenStats && (
                                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#3d4149]/20 text-[#6b7280]">
                                      <i className="ri-eye-off-line text-[10px]"></i>
                                      Privado
                                    </span>
                                  )}
                                </div>
                                <p className="text-[#6b7280] text-xs">
                                  {hasHiddenStats
                                    ? '— / — · Stats ocultas por el usuario'
                                    : `${player.won_bets}/${player.total_bets} ganadas · ${hitRate}% acc.`}
                                </p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                {hasHiddenStats ? (
                                  <div className="flex flex-col items-end gap-1">
                                    <div className="flex items-center gap-1.5 px-3 py-1 bg-[#1e2028] rounded-lg">
                                      <div className="w-3.5 h-3.5 flex items-center justify-center text-[#6b7280]">
                                        <i className="ri-eye-off-line text-xs"></i>
                                      </div>
                                      <p className="text-[#6b7280] font-medium text-xs">Oculto</p>
                                    </div>
                                    <p className="text-[#3d4149] text-xs">Elite · Privado</p>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-white font-bold tabular-nums">
                                      {player.current_bankroll.toLocaleString('es-ES')}u
                                    </p>
                                    <p
                                      className={`text-sm font-bold ${
                                        roi >= 0 ? 'text-[#00e87a]' : 'text-[#ef4444]'
                                      }`}
                                    >
                                      {roi >= 0 ? '+' : ''}{roi.toFixed(1)}% ROI
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {isPremium && myLeagueRanking.length > 0 && supabaseUser && (
                    <BankrollCharts
                      ranking={myLeagueRanking}
                      currentUserId={supabaseUser.id}
                      initialBankroll={leagues.find((l) => l.id === myLeagues[0])?.bankroll ?? 1000}
                    />
                  )}
                  {!isPremium && myLeagueRanking.length > 0 && (
                    <div className="mt-4 bg-[#ffd700]/5 border border-[#ffd700]/20 rounded-xl px-5 py-4 flex items-center gap-4">
                      <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#ffd700]/15 flex-shrink-0">
                        <i className="ri-bar-chart-2-line text-[#ffd700] text-lg"></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-[#ffd700] font-bold text-sm">Gráficas avanzadas — Premium</p>
                        <p className="text-[#a8b2c1] text-xs mt-0.5">
                          Evolución de bankroll, comparativa de rivales y análisis de rendimiento detallado.
                        </p>
                      </div>
                      <button
                        onClick={() => openPaywallModal('premium')}
                        className="px-4 py-2 bg-[#ffd700]/15 border border-[#ffd700]/30 text-[#ffd700] text-xs font-bold rounded-xl cursor-pointer hover:bg-[#ffd700]/25 transition-all whitespace-nowrap flex-shrink-0"
                      >
                        Activar Premium
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Crear Liga ────────────────────────── */}
          {activeTab === 'crear' && (
            <div className="space-y-6">
              {isRegistered ? (
                <CreateLeagueForm onLeagueCreated={handleLeagueCreated} />
              ) : (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-16 text-center max-w-lg mx-auto">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#ffd700]/10 mx-auto mb-4">
                    <i className="ri-user-line text-[#ffd700] text-3xl"></i>
                  </div>
                  <p className="text-white font-bold text-lg">Inicia sesión para crear una liga</p>
                  <p className="text-[#6b7280] text-sm mt-2 mb-6">Crea una cuenta gratuita y empieza a competir.</p>
                  <button
                    onClick={openLogin}
                    className="px-6 py-3 bg-[#ffd700] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#f0c800] transition-all whitespace-nowrap"
                  >
                    Iniciar sesión
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showJoinModal && selectedLeague && (
        <JoinModal
          league={selectedLeague}
          onClose={() => { setShowJoinModal(false); setSelectedLeague(null); }}
          onJoined={handleJoined}
        />
      )}

      <Footer />
    </div>
  );
}
